/**
 * How a member's highlight reads on the restaurant's side — its Messages, its
 * Fans page and its own page (Kapil, 2026-09-25). Pure, so it is unit-tested
 * (restaurantHighlights.spec.js) rather than trusted inside three pages.
 */
import { relativeAgo } from '@/utils/relativeAgo'

/**
 * The line above a highlight in the restaurant's Messages: "Highlight shared
 * by member who is a Fan of NAIA". A member shares only with a Favorite, so
 * they almost always still are one; if not, the line drops the claim.
 */
export function highlightMessageLabel({ restaurantName, memberIsFan } = {}) {
  return memberIsFan && restaurantName
    ? `Highlight shared by member who is a Fan of ${restaurantName}`
    : 'Highlight shared by member'
}

/** Whether a message in the thread is a member's highlight. */
export const isHighlightMessage = msg => !!(msg && msg.profileShareId && !msg.deletedAt)

/** "1d ago" — the app's time everywhere else (My Notes, Favorites). '' without a date. */
export function highlightAgo(at) {
  const ago = relativeAgo(at)

  return ago ? `${ago} ago` : ''
}

/**
 * What the Fans page shows under a member's bookings (Kapil, 2026-09-25):
 *  - 'highlight' — the note icon with the words the restaurant got, and when;
 *  - 'private'   — the filled note icon alone: they keep a note about this
 *                  restaurant "even if private", never a word of it;
 *  - null        — nothing.
 */
export function fanNoteLine(fan) {
  if (fan?.highlight?.text) {
    return { kind: 'highlight', text: fan.highlight.text, ago: highlightAgo(fan.highlight.sharedAt) }
  }
  if (fan?.hasNote) return { kind: 'private' }

  return null
}

/** What tapping the private-note icon says — the words a member's friends see. */
export function privateNoteHint(fan, restaurantName) {
  const who = fan?.firstName || 'This member'

  return `${who} has kept their notes for ${restaurantName || 'your restaurant'} private.`
}
